import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/admin-auth-context";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";

const ROLES = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "empleado", label: "Empleado" },
];

const ROLE_COLORS = {
  owner: "bg-blue-50 text-blue-700 border-blue-200",
  admin: "bg-emerald-50 text-emerald-700 border-emerald-200",
  empleado: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AdminUsers() {
  const { api, user: me } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/users");
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const onSave = async (data) => {
    try {
      if (data.id) {
        const payload = {
          name: data.name,
          role: data.role,
          ...(data.password ? { password: data.password } : {}),
        };
        await api.put(`/users/${data.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", {
          email: data.email,
          name: data.name,
          role: data.role,
          password: data.password,
        });
        toast.success("Usuario creado");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onDelete = async (u) => {
    if (u.id === me.id) {
      toast.error("No puedes eliminar tu propia cuenta");
      return;
    }
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
            Administración
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter mt-3 text-[var(--icor-navy)]">
            Usuarios y permisos
          </h1>
        </div>
        <button onClick={() => setEditing({ role: "empleado" })} className="btn-primary" data-testid="admin-users-new">
          <Plus size={16} weight="bold" /> Nuevo usuario
        </button>
      </div>

      <div
        className="bg-white border border-slate-200 overflow-x-auto"
        style={{ borderRadius: 4 }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {["Nombre", "Email", "Rol", "Creado", "Acciones"].map((h) => (
                <th
                  key={h}
                  className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-400">Cargando…</td>
              </tr>
            ) : (
              items.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-[var(--icor-navy)]">
                    {u.name}
                    {u.id === me?.id && (
                      <span className="ml-2 text-[10px] tracking-[0.18em] uppercase text-slate-400">(tú)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-bold border ${
                        ROLE_COLORS[u.role] || ""
                      }`}
                      style={{ borderRadius: 4 }}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {u.created_at
                      ? new Date(u.created_at).toLocaleDateString("es-MX", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditing({ ...u, password: "" })}
                      className="p-1.5 text-slate-500 hover:text-[var(--icor-blue)]"
                      aria-label="Editar"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => onDelete(u)}
                      className="p-1.5 text-slate-500 hover:text-red-600 ml-2"
                      aria-label="Eliminar"
                    >
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserDialog initial={editing} onClose={() => setEditing(null)} onSave={onSave} />
      )}
    </div>
  );
}

const UserDialog = ({ initial, onClose, onSave }) => {
  const [d, setD] = useState({ ...initial });
  const isEdit = !!initial.id;
  const submit = (e) => {
    e.preventDefault();
    onSave(d);
  };
  const upd = (k, v) => setD((s) => ({ ...s, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" data-testid="admin-users-dialog">
      <div className="bg-white w-full max-w-md" style={{ borderRadius: 4 }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-display text-xl font-semibold text-[var(--icor-navy)]">
            {isEdit ? "Editar usuario" : "Nuevo usuario"}
          </h2>
          <button onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {!isEdit && (
            <Field label="Email" required>
              <input
                type="email"
                required
                className="icor-input"
                value={d.email || ""}
                onChange={(e) => upd("email", e.target.value)}
                data-testid="admin-user-email"
              />
            </Field>
          )}
          <Field label="Nombre" required>
            <input
              required
              className="icor-input"
              value={d.name || ""}
              onChange={(e) => upd("name", e.target.value)}
              data-testid="admin-user-name"
            />
          </Field>
          <Field label="Rol" required>
            <select
              required
              className="icor-input"
              value={d.role || "empleado"}
              onChange={(e) => upd("role", e.target.value)}
              data-testid="admin-user-role"
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label={isEdit ? "Nueva contraseña (opcional)" : "Contraseña"} required={!isEdit}>
            <input
              type="password"
              required={!isEdit}
              className="icor-input"
              value={d.password || ""}
              onChange={(e) => upd("password", e.target.value)}
              minLength={6}
              placeholder={isEdit ? "Dejar en blanco para no cambiar" : "mínimo 6 caracteres"}
              data-testid="admin-user-password"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-dark">
              Cancelar
            </button>
            <button type="submit" className="btn-primary" data-testid="admin-user-save">
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
      {label}
      {required && <span className="text-[var(--icor-blue)] ml-1">*</span>}
    </span>
    {children}
  </label>
);
